/* =====================================================================
   ZIRAK PREMIUM  |  PRODUCT CARD IMAGE SWIPE + VIDEO
   Turns product cards into a swipeable gallery when that product has more
   than one photo, with a video slide when one is uploaded. Works on:
   - the shared Dawn product card (collections, search, "You may also like")
   - the homepage ZP product grid / sliders (.zp-card)
   - the Recently Viewed cards (.zrv-card)
   Cards that appear later (recommendations, recently viewed, filters) are
   picked up straight away (next animation frame) - no waiting for the page
   to go quiet, because some apps keep changing the page every second.

   How it works: for each card, once it scrolls near the screen, this asks
   Shopify's own public product JSON for that product's photo/video list and
   builds a small swipe strip over the card's image. If a product only has one
   photo, nothing is added and the card behaves exactly as before.
   ===================================================================== */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var cache = new Map();

  // keep Recently Viewed badge / heart / share above the swipe strip
  (function () {
    var st = document.createElement('style');
    st.textContent =
      '.zrv-card__media .zrv-actions,.zrv-card__media .zrv-card__badge{z-index:4 !important;}' +
      '.zp-card__frame .zp-swipe{border-radius:inherit;overflow:hidden;}' +
      '.zp-card__frame .zp-card__quick{z-index:4;}';
    document.head.appendChild(st);
  })();

  var ICON_CHEVRON_LEFT =
    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15 5l-7 7 7 7" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_CHEVRON_RIGHT =
    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 5l7 7-7 7" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_PLAY =
    '<svg viewBox="0 0 24 24" fill="#fff" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="11" fill="rgba(0,0,0,.35)"/><path d="M10 8l6 4-6 4V8z" fill="#fff"/></svg>';

  function sizedUrl(url, width) {
    try {
      var u = new URL(url, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  function fetchMedia(path) {
    if (cache.has(path)) return cache.get(path);
    var p = fetch(path + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    cache.set(path, p);
    return p;
  }

  function buildSwipe(mount, product, href) {
    if (mount.querySelector('.zp-swipe')) return; // already built for this card

    var media = product.media;
    var wrap = document.createElement('div');
    wrap.className = 'zp-swipe';

    var track = document.createElement('div');
    track.className = 'zp-swipe__track';
    wrap.appendChild(track);

    media.forEach(function (m, i) {
      var slide = document.createElement('div');
      slide.className = 'zp-swipe__slide';

      var a = document.createElement('a');
      a.href = href;
      a.setAttribute('aria-label', product.title + ' - image ' + (i + 1) + ' of ' + media.length);
      slide.appendChild(a);

      if (m.media_type === 'video') {
        var video = document.createElement('video');
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.preload = 'none';
        var sources = (m.sources || []).filter(function (s) { return s.mime_type === 'video/mp4'; });
        if (!sources.length) sources = m.sources || [];
        sources.sort(function (a, b) { return (a.width || 0) - (b.width || 0); });
        var chosen = sources[0];
        if (chosen) {
          var source = document.createElement('source');
          source.src = chosen.url;
          source.type = chosen.mime_type || 'video/mp4';
          video.appendChild(source);
        }
        if (m.preview_image) video.poster = sizedUrl(m.preview_image.src, 600);
        video.setAttribute('draggable', 'false');
        a.appendChild(video);
      } else if (m.media_type === 'external_video') {
        var img1 = document.createElement('img');
        img1.src = sizedUrl((m.preview_image || {}).src || '', 600);
        img1.alt = m.alt || product.title;
        img1.loading = 'lazy';
        img1.draggable = false;
        a.appendChild(img1);

        var embed = m.embed_url ||
          (m.host === 'youtube' ? 'https://www.youtube.com/embed/' + m.external_id :
            m.host === 'vimeo' ? 'https://player.vimeo.com/video/' + m.external_id : '');
        if (embed) {
          slide.dataset.embed = embed;
          var play = document.createElement('span');
          play.className = 'zp-swipe__play';
          play.innerHTML = ICON_PLAY;
          slide.appendChild(play);
        }
      } else {
        var img = document.createElement('img');
        img.src = sizedUrl(m.src || (m.preview_image || {}).src || '', 600);
        img.alt = m.alt || product.title;
        img.loading = i === 0 ? 'eager' : 'lazy';
        img.draggable = false;
        a.appendChild(img);
      }

      track.appendChild(slide);
    });

    // Dots
    var dots = document.createElement('div');
    dots.className = 'zp-swipe__dots';
    media.forEach(function (m, i) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'zp-swipe__dot';
      dot.setAttribute('aria-label', 'Show image ' + (i + 1));
      if (i === 0) dot.setAttribute('aria-current', 'true');
      dot.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        goTo(i, true);
      });
      dots.appendChild(dot);
    });
    wrap.appendChild(dots);

    // Prev / next (desktop hover only, hidden on touch via CSS)
    var prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'zp-swipe__nav zp-swipe__nav--prev';
    prevBtn.innerHTML = ICON_CHEVRON_LEFT;
    prevBtn.setAttribute('aria-label', 'Previous image');
    prevBtn.hidden = true;

    var nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'zp-swipe__nav zp-swipe__nav--next';
    nextBtn.innerHTML = ICON_CHEVRON_RIGHT;
    nextBtn.setAttribute('aria-label', 'Next image');

    prevBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      goTo(active - 1, true);
    });
    nextBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      goTo(active + 1, true);
    });
    wrap.appendChild(prevBtn);
    wrap.appendChild(nextBtn);

    var active = 0;

    function goTo(i, smooth) {
      i = Math.max(0, Math.min(media.length - 1, i));
      var w = track.clientWidth || 1;
      track.scrollTo({ left: i * w, behavior: smooth && !reducedMotion ? 'smooth' : 'auto' });
    }

    function setActive(i) {
      i = Math.max(0, Math.min(media.length - 1, i));
      active = i;
      var dotEls = dots.querySelectorAll('.zp-swipe__dot');
      for (var d = 0; d < dotEls.length; d++) {
        if (d === i) dotEls[d].setAttribute('aria-current', 'true');
        else dotEls[d].removeAttribute('aria-current');
      }
      prevBtn.hidden = i === 0;
      nextBtn.hidden = i === media.length - 1;

      var videos = track.querySelectorAll('video');
      for (var v = 0; v < videos.length; v++) {
        var slideEl = videos[v].closest('.zp-swipe__slide');
        var idx = Array.prototype.indexOf.call(track.children, slideEl);
        if (idx === i && !reducedMotion) {
          videos[v].play().catch(function () {});
        } else {
          videos[v].pause();
        }
      }
    }

    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var w = track.clientWidth || 1;
        setActive(Math.round(track.scrollLeft / w));
        ticking = false;
      });
    }, { passive: true });

    // External video: tap the play icon to load the embed in place
    var embedSlides = track.querySelectorAll('.zp-swipe__slide[data-embed]');
    embedSlides.forEach(function (slide) {
      var playBtn = slide.querySelector('.zp-swipe__play');
      if (!playBtn) return;
      playBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (slide.querySelector('iframe')) return;
        var iframe = document.createElement('iframe');
        var embedUrl = slide.dataset.embed;
        iframe.src = embedUrl + (embedUrl.indexOf('?') > -1 ? '&' : '?') + 'autoplay=1&muted=1';
        iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
        iframe.setAttribute('frameborder', '0');
        iframe.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:0';
        slide.appendChild(iframe);
        playBtn.style.display = 'none';
      });
    });

    setActive(0);
    mount.appendChild(wrap);
  }

  // Find the product link and the image box for each kind of card
  function partsOf(card) {
    var href = null, mount = null, l;
    if (card.classList.contains('zp-card')) {
      // homepage ZP product grid: the whole card is the link
      href = card.getAttribute('href');
      if (!href) { l = card.querySelector('a[href*="/products/"]'); href = l && l.getAttribute('href'); }
      mount = card.querySelector('.zp-card__frame');
    } else if (card.classList.contains('zrv-card')) {
      l = card.querySelector('a[href*="/products/"]');
      href = l && l.getAttribute('href');
      mount = card.querySelector('.zrv-card__media');
    } else {
      l = card.querySelector('a[href*="/products/"]');
      href = l && l.getAttribute('href');
      mount = card.querySelector('.card__media .media');
    }
    return { href: href, mount: mount };
  }

  function handleCard(card) {
    var p = partsOf(card);
    if (!p.href || !p.mount) return;
    var path = p.href.split('?')[0].split('#')[0];
    if (path.indexOf('/products/') === -1) return;

    fetchMedia(path).then(function (product) {
      if (!product || !Array.isArray(product.media) || product.media.length < 2) return;
      buildSwipe(p.mount, product, p.href);
    });
  }

  var SELECTOR = '.product-card-wrapper, .zp-card, .zrv-card';
  var io = null;
  function observer() {
    if (io) return io;
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        handleCard(entry.target);
      });
    }, { rootMargin: '300px 600px', threshold: 0 });
    return io;
  }

  function initSwipeCards(root) {
    var scope = root || document;
    var cards = scope.querySelectorAll(SELECTOR);
    cards.forEach(function (c) {
      if (c.dataset.zpSwipeInit) return;
      c.dataset.zpSwipeInit = '1';
      observer().observe(c);
    });
  }

  // Run a scan on the next frame when new cards arrive. Throttled (not debounced), so pages where
  // other apps change things every second no longer delay the swipe.
  var scanQueued = false;
  function queueScan() {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(function () {
      scanQueued = false;
      initSwipeCards();
    });
  }
  function hasCard(node) {
    if (!node || node.nodeType !== 1) return false;
    return (node.matches && node.matches(SELECTOR)) || !!(node.querySelector && node.querySelector(SELECTOR));
  }

  function start() {
    initSwipeCards();
    if ('MutationObserver' in window) {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            if (hasCard(added[j])) { queueScan(); return; }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
    // safety net for cards that slip past (cheap: only new cards are touched)
    var n = 0;
    var iv = setInterval(function () { initSwipeCards(); if (++n > 20) clearInterval(iv); }, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  // Re-scan when a section re-renders in the theme editor (e.g. settings change)
  document.addEventListener('shopify:section:load', function (e) { initSwipeCards(e.target); });
})();
